import React, { useCallback, useRef, useState } from 'react';
import ModalDialog from '../../../../components/ModalDialog';
import Button from '../../../../components/Button';
import EventForm, { EventFormRef } from './EventForm';
import { CreateEventFormData, EditEventModalProps } from './types';
import { useEventMutation } from '../hooks/useEventMutations';
import { getEditEventFormValues } from '../utils/formUtils';

const EditEventModal: React.FC<EditEventModalProps> = ({
  isOpen,
  onClose,
  event,
  calendarId,
  onEventUpdated,
  calendarApi,
}) => {
  const formRef = useRef<EventFormRef>(null);
  const isSubmittingRef = useRef(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const defaultValues = getEditEventFormValues(event);
  const { handleUpdateEvent } = useEventMutation();

  const handleSubmit = async (data: CreateEventFormData) => {
    if (!event) return;

    const success = await handleUpdateEvent(
      calendarId,
      event.id,
      data,
      onEventUpdated,
      calendarApi,
    );

    if (success) {
      onClose();
    }
  };

  const handlePrimaryAction = () => {
    if (isSubmittingRef.current) return;
    if (!formRef.current) return;
    isSubmittingRef.current = true;
    formRef.current.submit();
  };

  const handleSubmittingChange = useCallback((submitting: boolean) => {
    setIsSubmitting(submitting);
    if (!submitting) isSubmittingRef.current = false;
  }, []);

  return (
    <ModalDialog
      isOpen={isOpen}
      setClosed={onClose}
      title="Edit Event"
      width="small"
      overflow="auto"
      fullHeight
      primaryActionOnClick={handlePrimaryAction}
      primaryActionOnEnter
      rightFooter={
        <Button
          title="Update Event"
          onClick={handlePrimaryAction}
          disabled={isSubmitting}
        />
      }
      headerClassName="py-2"
      bodyClassName="pt-4"
    >
      <EventForm
        ref={formRef}
        defaultValues={defaultValues}
        onSubmit={handleSubmit}
        isAllDay={defaultValues.isAllDay}
        onSubmittingChange={handleSubmittingChange}
        onValidationFailed={() => {
          isSubmittingRef.current = false;
        }}
        onSubmitRequest={handlePrimaryAction}
      />
    </ModalDialog>
  );
};

export default EditEventModal;
